import { useMemo } from 'react';
import type { Column } from '../../Table/Table';
import styled from 'styled-components';
import { ScoreBreakdownType, type TeamBehavior } from '../../../lib/types/TeamBehavior';
import { BehaviorBar } from '../components/BehaviorBar/BehaviorBar';
import { BehaviorType } from '../enum/BehaviorType';
import getTeamBehaviorDetails from '../utils/getTeamBehaviorDetails';
import { NudgeOptionMenu } from '../components/NudgeBehavior/NudgeOptionMenu';
import type { NudgeModalConfigs } from '../../Nudge/NudgeModal';
import { BehaviourTrend } from '../components/Trend/Trend';
import { getBehaviorTrendPercentage, getBehaviorTrendType } from '../utils/getBehaviorTrend';
import { Ellipsis } from '../../utility/Ellipsis';

type TeamBehaviorsData = {
  columns: Column<TeamBehavior>[];
};

export const useTeamBehaviorsData = (
  totalCount: number,
  setNudgeModalConfigs: (modalProps: NudgeModalConfigs) => void,
  onClickBehavior: (behavior: ScoreBreakdownType, type: BehaviorType) => void,
): TeamBehaviorsData => {
  return useMemo(
    () => ({
      columns: [
        {
          header: '',
          valueSelector: (teamBehaviorDetails: TeamBehavior) => (
            <BehaviorBar totalCount={totalCount} teamBehaviorDetails={teamBehaviorDetails} onClick={onClickBehavior} />
          ),
          alignment: 'left',
          width: '15fr',
        },
        {
          header: '',
          valueSelector: (teamBehaviorDetails: TeamBehavior) => {
            const mappingBehaviorDetails = getTeamBehaviorDetails(teamBehaviorDetails.behaviour);
            return (
              <BehaviorTypeDetails>
                {mappingBehaviorDetails?.icon}
                <Ellipsis>{mappingBehaviorDetails?.displayName}</Ellipsis>
              </BehaviorTypeDetails>
            );
          },
          alignment: 'left',
          width: '6fr',
        },
        {
          header: '',
          valueSelector: (teamBehaviorDetails: TeamBehavior) => (
            <TrendCell>
              <BehaviourTrend
                value={Math.round(Math.abs(getBehaviorTrendPercentage(teamBehaviorDetails.trend)))}
                type={getBehaviorTrendType(teamBehaviorDetails.trend)}
              />
            </TrendCell>
          ),
          alignment: 'right',
          width: '1.5fr',
        },
        {
          header: '',
          valueSelector: (teamBehaviorDetails: TeamBehavior) => (
            <NudgeOptionMenu
              behavior={teamBehaviorDetails.behaviour}
              learners={teamBehaviorDetails.learners}
              setNudgeModalConfigs={setNudgeModalConfigs}
              hideMenu={teamBehaviorDetails.learners.strong === 0 && teamBehaviorDetails.learners.weak === 0}
            />
          ),
          alignment: 'right',
          width: '36px',
        },
      ],
    }),
    [totalCount, onClickBehavior, setNudgeModalConfigs],
  );
};

const BehaviorTypeDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  width: 100%;
  color: ${({ theme }) => theme.colors.prototype.foreground};
  svg {
    flex-shrink: 0;
  }

  ${Ellipsis} {
    flex: 1;
    min-width: 0;
  }
`;

const TrendCell = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;
